<script setup lang="ts">
import {
  Activity as ActivityIcon,
  ArrowDownRight as ArrowDownRightIcon,
  ArrowUpRight as ArrowUpRightIcon,
  Bike as BikeIcon,
  Car as CarIcon,
  ChevronDown as ChevronDownIcon,
  ChevronRight as ChevronRightIcon,
  CircleSlash2 as CircleSlash2Icon,
  Clock as ClockIcon,
  Dumbbell as DumbbellIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  Footprints as FootprintsIcon,
  Loader2 as Loader2Icon,
  MoreHorizontal as MoreHorizontalIcon,
  Pencil as PencilIcon,
  Pin as PinIcon,
  PinOff as PinOffIcon,
  Plane as PlaneIcon,
  Plus as PlusIcon,
  Route as RouteIcon,
  Ruler as RulerIcon,
  Sailboat as SailboatIcon,
  Copy as CopyIcon,
  Download as DownloadIcon,
  SquareMinus as SquareMinusIcon,
  SquarePlus as SquarePlusIcon,
  Maximize as MaximizeIcon,
  Tag as TagIcon,
  Train as TrainIcon,
  Trash2 as Trash2Icon,
  Undo2 as Undo2Icon,
  Waves as WavesIcon,
  X as XIcon,
} from '@lucide/vue'
import { ref, computed, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'

import MobileActivityTypeActionSheet from '@/components/map/MobileActivityTypeActionSheet.vue'
import GpxDownloadOptionsModal from '@/components/map/modals/GpxDownloadOptionsModal.vue'
import TourDeleteModal from '@/components/map/modals/TourDeleteModal.vue'
import POIDetailPanel from '@/components/map/toolbar/POIDetailPanel.vue'
import RoutePointList from '@/components/map/toolbar/RoutePointList.vue'
import TrackActionMenu from '@/components/map/toolbar/TrackActionMenu.vue'
import WaypointList from '@/components/map/toolbar/WaypointList.vue'
import BaseActionButton from '@/components/ui/BaseActionButton.vue'
import TourTypeSelect from '@/components/ui/TrackAndTourTypeSelect.vue'
import { useEventListener } from '@/composables/useEventListener'
import {
  openAddToCollection as openAddToCollectionPicker,
  tourToAddToCollectionItem,
} from '@/services/addToCollectionService'
import { apiService } from '@/services/apiService'
import { showConfirm } from '@/services/dialogService'
import { downloadFile } from '@/services/downloadService'
import { formatTime } from '@/services/formattingService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { hasPermission, requirePermission } from '@/services/permissionService'
import { toggleTourPinned } from '@/services/pinnedService'
import { showError } from '@/services/toastService'
import {
  getTrackTypeCategory,
  getTrackTypeName,
  shouldHideAscentForTour,
  shouldHideAscentForTrack,
} from '@/services/tourTypeService'
import { TrackColorService } from '@/services/trackColorService'
import {
  calculateTrackDistance,
  getTrackElevationGain,
  getTrackElevationLoss,
  getTrackTimeElapsed,
  getTrackTimeMoving,
} from '@/services/trackMetricsCalculationService'
import { isSmallScreen } from '@/services/uiService'
// Waypoint icon handling moved to WaypointList component
import { useAuthStore } from '@/stores/authStore'
import { useLibraryStore } from '@/stores/libraryStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { Tour, Track } from '@/types/tour'
import { getCreatorByline } from '@/utils/creatorByline'
import { requireItemEditable } from '@/utils/permissionDialog'
import { resolveTourAccentColor } from '@/utils/tourAccentColor'
import {
  getEffectiveTourName,
  getTourGlobalNameSubtitle,
  getWorkspaceTourDownloadName,
  hasCollectionNameOverride,
} from '@/utils/tourName'
import {
  getRootSourceTour,
  getRootSourceTourClientId,
  isDownloadableTourSourceFilename,
} from '@/utils/tourSource'

import TrackEditModal from '../modals/TrackEditModal.vue'

const props = withDefaults(
  defineProps<{
    tour: Tour
    loading?: boolean
    allowTrackAdd?: boolean
    allowTrackRouteEdit?: boolean
    selectedWaypointIndex?: number | null
    isLibraryContext?: boolean
    waypointOpenSource?: 'list' | 'map' | null
    /**
     * Embedded mode for the mobile Route sheet: hides the top header row, the stats grid,
     * the Tracks/Waypoints/Route-points sections (those move to their own tab + the shared
     * stats row), keeping name/date/type/quick-actions/description/secondary-actions.
     */
    embedded?: boolean
  }>(),
  {
    allowTrackAdd: true,
    allowTrackRouteEdit: true,
    selectedWaypointIndex: null,
    isLibraryContext: false,
    waypointOpenSource: null,
    embedded: false,
  },
)

const emit = defineEmits<{
  'zoom-tour': [tourId: string]
  'zoom-track': [tourId: string, trackIndex: number]
  'zoom-waypoint': [tourId: string, waypointIndex: number]
  'zoom-route-point': [tourId: string, routePointIndex: number]
  back: []
  'open-waypoint-detail': [tourId: string, waypointIndex: number, source?: 'list' | 'map']
  'close-waypoint-detail': []
  'delete-waypoint': [tourId: string, waypointIndex: number]
  'edit-waypoint': [tourId: string, waypointIndex: number]
  'enter-track-edit': [tourId: string, trackClientId: string]
  'retain-unpinned-tour-detail': [tour: Tour]
}>()

const workspaceStore = useWorkspaceStore()
const libraryStore = useLibraryStore()
const uiStore = useUiStore()
const authStore = useAuthStore()
const router = useRouter()

const isTracksExpanded = ref(true)
const isWaypointsExpanded = ref(true)
const isRoutePointsExpanded = ref(true)
const isEditTrackModalOpen = ref(false)
const isDeleteTourModalOpen = ref(false)
const isDeletingTour = ref(false)
// Waypoint edit modal handled in WaypointList
const editingTrackIndex = ref(-1)
const downloadTrackIndex = ref<number | null>(null)
const isTrackDownloadModalOpen = ref(false)
const openTrackToolbarIds = ref<Set<number>>(new Set())
const downloadingTrackIds = ref<Set<string>>(new Set())
const isTourColorPickerOpen = ref(false)
const tourColorButtonRef = ref<HTMLElement | null>(null)
const tourColorPickerStyle = ref<Record<string, string>>({})
const tourAccentColors = TrackColorService.getColors()

// Tour icon + color (mirrors TourList logic)
const isTourActivity = computed(
  () => !!props.tour.type || props.tour.tracks.some((t) => !!t.startTime),
)

const isTourVisible = computed(() => props.tour.visible !== false)
const handleToggleTourVisibility = () => {
  workspaceStore.setTourVisibility(props.tour.clientId, !isTourVisible.value)
}

const handleTogglePinned = async () => {
  if (!authStore.isAuthenticated) {
    window.dispatchEvent(new CustomEvent('open-auth-modal', { detail: { view: 'login' } }))
    return
  }
  if (props.tour.isPinned) {
    emit('retain-unpinned-tour-detail', props.tour)
  }
  await toggleTourPinned(props.tour, { preserveDetailSelection: true })
}

// Creator-only edits remain visible and explanatory. Whole-tour Delete is the documented hidden
// exception because collection-role changes cannot grant creator ownership.
const canEditTourContent = computed(() => hasPermission('edit_track'))
const canDeleteTourContent = computed(() => hasPermission('delete_track'))
const canEditWaypointContent = computed(() => hasPermission('edit_waypoint'))
const canDeleteWaypointContent = computed(() => hasPermission('delete_waypoint'))
const isPinnedWorkspaceContext = computed(
  () => workspaceStore.isSystem === true && workspaceStore.systemKey === 'pinned',
)
const canDeleteEntireTour = computed(
  () => hasPermission('delete_tour') && props.tour.canEdit !== false,
)
const canRemoveTourFromCollection = computed(
  () => hasPermission('delete_tour') && !isPinnedWorkspaceContext.value,
)
const tourTypeIcon = computed(() => {
  if (!props.tour.type) {
    return isTourActivity.value ? ActivityIcon : RouteIcon
  }
  const category = getTrackTypeCategory(props.tour.type)
  switch (category) {
    case 'aviation':
      return PlaneIcon
    case 'cycling':
      return BikeIcon
    case 'driving':
      return CarIcon
    case 'rail':
      return TrainIcon
    case 'hiking':
    case 'running':
    case 'walking':
      return FootprintsIcon
    case 'swimming':
    case 'diving':
      return WavesIcon
    case 'water_sports':
      return SailboatIcon
    case 'gym_fitness_equipment':
      return DumbbellIcon
    default:
      return ActivityIcon
  }
})
const tourColor = computed(() => {
  return resolveTourAccentColor(props.tour)
})
const canChangeTourColor = computed(() => canEditTourContent.value && props.tour.tracks.length > 0)
const effectiveTourName = computed(() => getEffectiveTourName(props.tour))
const tourCreatorByline = computed(() =>
  props.isLibraryContext ? null : getCreatorByline(props.tour),
)
const tourNameSubtitle = computed(() => getTourGlobalNameSubtitle(props.tour))
const hasCollectionNameReset = computed(() => hasCollectionNameOverride(props.tour))
const tourNameEditPlaceholder = computed(() =>
  !props.isLibraryContext && hasCollectionNameReset.value ? props.tour.name : '',
)
const tourColorButtonTitle = computed(() => {
  if (!canEditTourContent.value) return 'You do not have permission to change the tour color'
  if (props.tour.tracks.length === 0) return 'Add a track before choosing a tour color'
  return 'Change tour color'
})

const toggleTourColorPicker = async () => {
  if (!canChangeTourColor.value) return
  if (!(await requirePermission('edit_track'))) return
  isTourColorPickerOpen.value = !isTourColorPickerOpen.value
  if (isTourColorPickerOpen.value) {
    await nextTick()
    updateTourColorPickerPosition()
  }
}

const selectTourColor = async (color: string) => {
  if (!canChangeTourColor.value) return
  if (!(await requirePermission('edit_track'))) return

  await workspaceStore.updateTrack(props.tour.clientId, 0, { color })
  isTourColorPickerOpen.value = false
}

const getColorTextColor = (bgColor: string): string => {
  return TrackColorService.getTextColor(bgColor)
}

const updateTourColorPickerPosition = () => {
  if (!tourColorButtonRef.value) return
  const rect = tourColorButtonRef.value.getBoundingClientRect()
  const pickerWidth = 208
  const viewportWidth = window.innerWidth
  const left = Math.min(Math.max(rect.right - pickerWidth, 8), viewportWidth - pickerWidth - 8)

  tourColorPickerStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 8}px`,
    left: `${left}px`,
  }
}

// Activity type — live-sync to current tour, save on change
const tourType = ref<string | undefined>(props.tour.type ?? undefined)
const isMobileActivityTypeSheetOpen = ref(false)
const isSavingMobileActivityType = ref(false)
const mobileActivityTypeError = ref('')
watch(
  () => props.tour.type,
  (val) => {
    tourType.value = val ?? undefined
  },
)
const handleTourTypeChange = (newType: string | undefined) => {
  if (!canEditTourContent.value) return
  tourType.value = newType
  workspaceStore.updateTour(props.tour.clientId, { type: newType })
}
const tourTypeDisplay = computed(
  () => getTrackTypeName(tourType.value) ?? tourType.value ?? 'Activity',
)

const openMobileActivityTypeSheet = async () => {
  if (!canEditTourContent.value) return
  if (!(await requirePermission('edit_track'))) return
  mobileActivityTypeError.value = ''
  isMobileActivityTypeSheetOpen.value = true
}

const closeMobileActivityTypeSheet = () => {
  if (isSavingMobileActivityType.value) return
  isMobileActivityTypeSheetOpen.value = false
  mobileActivityTypeError.value = ''
}

const saveMobileActivityType = async (newType: string) => {
  if (!canEditTourContent.value || isSavingMobileActivityType.value) return
  if (newType === (tourType.value ?? '')) {
    closeMobileActivityTypeSheet()
    return
  }

  mobileActivityTypeError.value = ''
  isSavingMobileActivityType.value = true
  try {
    await workspaceStore.updateTour(props.tour.clientId, { type: newType })
    tourType.value = newType || undefined
    isMobileActivityTypeSheetOpen.value = false
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to update activity type from mobile sheet', {
      error,
      tourId: props.tour.clientId,
      type: newType,
    })
    mobileActivityTypeError.value = `Failed to update activity type: ${
      error instanceof Error ? error.message : String(error)
    }`
  } finally {
    isSavingMobileActivityType.value = false
  }
}

// Inline name editing
const isEditingName = ref(false)
const editedName = ref('')
const nameInputRef = ref<HTMLInputElement | null>(null)

const startEditingName = async () => {
  if (!canEditTourContent.value) return
  if (!(await requirePermission('edit_track'))) return
  editedName.value = effectiveTourName.value
  isEditingName.value = true
  nextTick(() => {
    nameInputRef.value?.select()
  })
}

const saveTourName = async () => {
  if (!isEditingName.value) return
  const trimmed = editedName.value.trim()
  isEditingName.value = false
  if (!canEditTourContent.value) return
  if (!(await requirePermission('edit_track'))) return

  if (props.isLibraryContext) {
    if (!trimmed || trimmed === props.tour.name) return
    await libraryStore.updateTour(props.tour.clientId, { name: trimmed })
    return
  }

  if (!trimmed) {
    await workspaceStore.updateTourCollectionName(props.tour.clientId, null)
    return
  }

  if (trimmed === effectiveTourName.value) return
  await workspaceStore.updateTourCollectionName(props.tour.clientId, trimmed)
}

const cancelEditingName = () => {
  isEditingName.value = false
}

const resetCollectionName = async () => {
  if (!hasCollectionNameReset.value) return
  if (!canEditTourContent.value) return
  if (!(await requirePermission('edit_track'))) return
  isEditingName.value = false
  await workspaceStore.updateTourCollectionName(props.tour.clientId, null)
}

// Inline description editing
const isEditingDescription = ref(false)
const descriptionExpanded = ref(false)
const editedDescription = ref('')
const descriptionError = ref('')
const descriptionTextareaRef = ref<HTMLTextAreaElement | null>(null)

const startEditingDescription = async () => {
  if (!canEditTourContent.value) return
  if (!(await requirePermission('edit_track'))) return
  editedDescription.value = props.tour.description || ''
  descriptionError.value = ''
  descriptionExpanded.value = true
  isEditingDescription.value = true
  nextTick(() => {
    const el = descriptionTextareaRef.value
    if (el) {
      el.style.height = 'auto'
      el.style.height = el.scrollHeight + 'px'
      el.focus()
    }
  })
}

const saveTourDescription = async () => {
  if (!isEditingDescription.value) return
  if (editedDescription.value.length > 2048) {
    descriptionError.value = 'Description must be 2048 characters or fewer'
    return
  }
  if (!canEditTourContent.value) return
  if (!(await requirePermission('edit_track'))) return
  workspaceStore.updateTour(props.tour.clientId, { description: editedDescription.value })
  isEditingDescription.value = false
  descriptionError.value = ''
}

const cancelEditingDescription = () => {
  isEditingDescription.value = false
  descriptionError.value = ''
}

const sourceTour = computed(() => props.tour.sourceTour ?? null)
const sourceFileLabel = computed(() => {
  const label = props.tour.sourceFilename?.trim()
  if (
    sourceTour.value ||
    !props.tour.canDownloadOriginal ||
    !isDownloadableTourSourceFilename(label)
  ) {
    return undefined
  }
  return label
})
const sourceFileDownloadName = computed(() => props.tour.sourceFilename?.trim() || 'tour-source')
const sourceFileHref = computed(() => apiService.getTourSourceFileUrl(props.tour.clientId))
const sourceTourPrimaryWorkspace = computed(() => sourceTour.value?.workspaces?.[0] ?? null)
const sourceTourHref = computed(() => {
  const source = sourceTour.value
  if (!source) return '#'
  const workspace = sourceTourPrimaryWorkspace.value
  if (!workspace) return router.resolve({ name: 'workspaces' }).href
  return router.resolve({
    name: 'tour-detail',
    params: { workspaceId: workspace.clientId, tourId: source.clientId },
  }).href
})
const handleSourceTourClick = (event: MouseEvent) => {
  const source = sourceTour.value
  if (!source) return
  event.preventDefault()

  const workspace = sourceTourPrimaryWorkspace.value
  if (workspace) {
    router.push({
      name: 'tour-detail',
      params: { workspaceId: workspace.clientId, tourId: source.clientId },
    })
    return
  }

  uiStore.isAllActivitiesView = true
  libraryStore.selectTourForDetail(
    {
      clientId: source.clientId,
      name: source.name,
      description: null,
      type: null,
      distance: null,
      elevationGain: null,
      createdAt: null,
      updatedAt: null,
      workspaces: source.workspaces,
      tracks: [],
    },
    { focusMap: false },
  )
}

// Waypoint visibility handled within WaypointList

const toggleTracksExpanded = () => {
  isTracksExpanded.value = !isTracksExpanded.value
}

// Waypoint expansion toggled via child component events

// Waypoint visibility toggle delegated to child component

const handleZoomTrack = (tourId: string, trackIndex: number) => {
  emit('zoom-track', tourId, trackIndex)
}

const handleZoomWaypoint = (tourId: string, waypointIndex: number) => {
  emit('zoom-waypoint', tourId, waypointIndex)
}

const handleZoomRoutePoint = (tourId: string, routePointIndex: number) => {
  emit('zoom-route-point', tourId, routePointIndex)
}

const formatDistance = (meters: number): string => {
  return (meters / 1000).toFixed(1) + ' km'
}

const calculateTourDistance = (): number => {
  let totalDistance = 0

  props.tour.tracks.forEach((track) => {
    totalDistance += calculateTrackDistance(track)
  })

  return totalDistance
}

const calculateTourTimeElapsed = (): number | null => {
  // Use tour-level time_elapsed if available, otherwise sum from tracks
  if (props.tour.time_elapsed !== null && props.tour.time_elapsed !== undefined) {
    return props.tour.time_elapsed
  }

  let totalTime: number | null = null
  props.tour.tracks.forEach((track) => {
    const trackTime = getTrackTimeElapsed(track)
    if (trackTime !== null) {
      totalTime = (totalTime || 0) + trackTime
    }
  })

  return totalTime
}

const calculateTourTimeMoving = (): number | null => {
  // Use tour-level time_moving if available, otherwise sum from tracks
  if (props.tour.time_moving !== null && props.tour.time_moving !== undefined) {
    return props.tour.time_moving
  }

  let totalTime: number | null = null
  props.tour.tracks.forEach((track) => {
    const trackTime = getTrackTimeMoving(track)
    if (trackTime !== null) {
      totalTime = (totalTime || 0) + trackTime
    }
  })

  return totalTime
}

const goBack = () => {
  emit('back')
}

const selectedWaypoint = computed(() => {
  const index = props.selectedWaypointIndex
  if (index === null || index === undefined) return null
  if (index < 0 || index >= props.tour.waypoints.length) return null
  return props.tour.waypoints[index] ?? null
})
const isSelectedWaypointIndividuallyVisible = computed(() => {
  const index = props.selectedWaypointIndex
  if (index === null || index === undefined) return true
  return !uiStore.isWaypointHidden(`${props.tour.clientId}_${index}`)
})
const headerIsWaypointMode = computed(() => !!selectedWaypoint.value)
const headerVisibilityTitle = computed(() =>
  headerIsWaypointMode.value
    ? isSelectedWaypointIndividuallyVisible.value
      ? 'Hide on map'
      : 'Show on map'
    : isTourVisible.value
      ? 'Hide on map'
      : 'Show on map',
)
const headerVisibilityIcon = computed(() =>
  headerIsWaypointMode.value
    ? isSelectedWaypointIndividuallyVisible.value
      ? EyeIcon
      : EyeOffIcon
    : isTourVisible.value
      ? EyeIcon
      : EyeOffIcon,
)

const waypointDetailKey = computed(
  () => `${props.tour.clientId}:${props.selectedWaypointIndex ?? 'tour-detail'}`,
)

const panelContentTransitionName = ref('tour-detail-drill-forward')
const lastWaypointDrillSource = ref<'list' | 'map' | null>(null)
const tourContentScrollRef = ref<HTMLElement | null>(null)
const savedTourContentScrollTop = ref(0)
const shouldRestoreTourScrollAfterEnter = ref(false)

watch(
  () => selectedWaypoint.value?.clientId ?? null,
  (nextWaypointId, previousWaypointId) => {
    if (nextWaypointId && !previousWaypointId) {
      savedTourContentScrollTop.value = tourContentScrollRef.value?.scrollTop ?? 0
      lastWaypointDrillSource.value = props.waypointOpenSource
      panelContentTransitionName.value =
        props.waypointOpenSource === 'list' ? 'tour-detail-drill-forward' : 'tour-detail-drill-none'
      return
    }

    if (!nextWaypointId && previousWaypointId) {
      panelContentTransitionName.value = 'tour-detail-drill-back'
      shouldRestoreTourScrollAfterEnter.value = true
      return
    }

    if (nextWaypointId && previousWaypointId && nextWaypointId !== previousWaypointId) {
      lastWaypointDrillSource.value = props.waypointOpenSource
      panelContentTransitionName.value =
        props.waypointOpenSource === 'list' ? 'tour-detail-drill-forward' : 'tour-detail-drill-none'
    }
  },
)

watch(
  () => [props.selectedWaypointIndex, props.tour.clientId, props.tour.waypoints.length] as const,
  ([waypointIndex]) => {
    if (waypointIndex === null || waypointIndex === undefined) return
    if (waypointIndex < 0 || waypointIndex >= props.tour.waypoints.length) {
      emit('close-waypoint-detail')
    }
  },
)

const handleOpenWaypointDetail = (
  tourId: string,
  waypointIndex: number,
  source: 'list' | 'map' = 'list',
) => {
  emit('open-waypoint-detail', tourId, waypointIndex, source)
}

const handleCloseWaypointDetail = () => {
  emit('close-waypoint-detail')
}

const handleDeleteWaypointRequest = (tourId: string, waypointIndex: number) => {
  emit('delete-waypoint', tourId, waypointIndex)
}

const handleHeaderCenter = () => {
  if (
    selectedWaypoint.value &&
    props.selectedWaypointIndex !== null &&
    props.selectedWaypointIndex !== undefined
  ) {
    emit('zoom-waypoint', props.tour.clientId, props.selectedWaypointIndex)
    return
  }

  emit('zoom-tour', props.tour.clientId)
}

const handleHeaderVisibilityToggle = () => {
  if (selectedWaypoint.value) {
    toggleWaypointVisibility()
    return
  }

  handleToggleTourVisibility()
}

const prepareTourScrollPosition = (element: Element) => {
  if (!shouldRestoreTourScrollAfterEnter.value) return
  ;(element as HTMLElement).style.visibility = 'hidden'
}

const restorePreparedTourScrollPosition = (element: Element) => {
  if (!shouldRestoreTourScrollAfterEnter.value) return
  requestAnimationFrame(() => {
    const scrollContainer = tourContentScrollRef.value
    if (scrollContainer) {
      scrollContainer.scrollTop = savedTourContentScrollTop.value
      void scrollContainer.offsetHeight
    }
    requestAnimationFrame(() => {
      ;(element as HTMLElement).style.visibility = ''
    })
  })
}

const resetPreparedTourVisibility = (element: Element) => {
  ;(element as HTMLElement).style.visibility = ''
}

const finalizeTourScrollRestore = () => {
  if (!shouldRestoreTourScrollAfterEnter.value) return
  shouldRestoreTourScrollAfterEnter.value = false
  if (tourContentScrollRef.value) {
    tourContentScrollRef.value.scrollTop = savedTourContentScrollTop.value
  }
}

const toggleTrackVisibility = (trackIndex: number, event: Event) => {
  event.stopPropagation()
  const track = props.tour.tracks[trackIndex]
  if (track) {
    const newVisibility = track.visible === undefined ? false : !track.visible
    workspaceStore.setTrackVisibility(props.tour.clientId, trackIndex, newVisibility)
  }
}

const toggleAllTracksVisibility = (visible: boolean) => {
  workspaceStore.toggleAllTracksVisibility(props.tour.clientId, visible)
}

const isAnyTrackVisible = computed(() => {
  return props.tour.tracks.some((track, index) => isTrackVisible(index))
})

const isTrackVisible = (trackIndex: number) => {
  const track = props.tour.tracks[trackIndex]
  return track.visible !== undefined ? track.visible : (props.tour.visible ?? true)
}

const showRoutePointsSection = computed(
  () =>
    uiStore.isTrackEditMode &&
    uiStore.editingTrackTourId === props.tour.clientId &&
    (props.tour.routePoints?.length ?? 0) > 0,
)

const isEditingTrack = (trackClientId?: string | null) =>
  uiStore.isTrackEditMode &&
  uiStore.editingTrackTourId === props.tour.clientId &&
  (uiStore.editingTrackClientId ?? null) === (trackClientId ?? null)

const isRouteEditButtonDisabled = () => uiStore.isTrackEditMode

const effectiveWaypointVisibility = computed(() => {
  const tourWaypointsVisible = props.tour._waypointsVisible ?? true
  return props.tour.visible !== false && tourWaypointsVisible && uiStore.areWaypointsVisible
})

const toggleWaypointVisibility = () => {
  const index = props.selectedWaypointIndex
  if (index === null || index === undefined) return
  uiStore.toggleSingleWaypointVisibility(`${props.tour.clientId}_${index}`)
}

const setTourWaypointsVisible = (visible: boolean) => {
  // The map renders waypoints from workspaceStore.tours (also in the all-activities/library view via
  // the proxy), so drive visibility there. In the library the detail tour is a separate object, so
  // keep its flag in sync too for the header's own effectiveWaypointVisibility state.
  workspaceStore.setTourWaypointVisibility(props.tour.clientId, visible)
  const detail = libraryStore.detailTour
  if (props.isLibraryContext && detail && detail.clientId === props.tour.clientId) {
    detail._waypointsVisible = visible
  }
}

// Whole-tour "Hide all waypoints" toggle for the Waypoints section header. Mirrors
// WaypointList.toggleWaypointVisibility: if the global waypoint layer is off, turn it back on (a
// per-tour toggle would otherwise be invisible); otherwise flip this tour's waypoint visibility.
const toggleAllWaypointsVisibility = () => {
  if (!uiStore.areWaypointsVisible) {
    uiStore.setWaypointsVisible(true)
    setTourWaypointsVisible(true)
    return
  }
  setTourWaypointsVisible(!(props.tour._waypointsVisible ?? true))
}

const handleAddWaypoint = () => {
  requirePermission('edit_waypoint').then((allowed) => {
    if (!allowed) return
    window.dispatchEvent(new CustomEvent('open-add-waypoint-modal'))
  })
}

const deleteAllWaypoints = async () => {
  if (!(await requirePermission('delete_waypoint'))) return
  const waypointCount = props.tour.waypoints.length
  if (waypointCount === 0) return
  // Check creator-only content edit *before* the "are you sure" prompt, so a non-creator sees
  // only the explainer instead of a confirm they can't actually act on.
  if (!(await requireItemEditable(props.tour.canEdit, 'waypoint', props.tour.createdByName))) return

  const confirmed = await showConfirm({
    title: 'Delete All Waypoints',
    message: `Are you sure you want to delete all ${waypointCount} waypoint${waypointCount > 1 ? 's' : ''}? This action cannot be undone.`,
    confirmText: 'Delete All',
    cancelText: 'Cancel',
    variant: 'danger',
  })

  if (!confirmed) return

  for (let i = props.tour.waypoints.length - 1; i >= 0; i--) {
    if (!workspaceStore.clientId) {
      await libraryStore.deleteWaypoint(props.tour.clientId, i)
    } else {
      workspaceStore.deleteWaypoint(props.tour.clientId, i)
    }
  }
}

const getRouteEditButtonTitle = (track: Track) => {
  if (!uiStore.isTrackEditMode) return 'Edit track route'
  if (isEditingTrack(track.clientId)) return 'This track route is currently being edited'
  return 'Finish the current track edit to switch tracks'
}

const getTrackColor = (track: Track): string => {
  return TrackColorService.getTourColor(props.tour.clientId, track.color)
}

const isTrackDownloading = (track: Track): boolean => {
  if (!track.clientId) {
    return false
  }
  return downloadingTrackIds.value.has(track.clientId)
}

const isTrackDownloadModalDownloading = computed(() => {
  const trackIndex = downloadTrackIndex.value
  if (trackIndex === null) return false
  const track = props.tour.tracks[trackIndex]
  return !!track && isTrackDownloading(track)
})

const downloadTrackGpx = async (trackIndex: number, includeRoutePoints: boolean) => {
  const track = props.tour.tracks[trackIndex]
  if (!track || !track.clientId) {
    return
  }

  downloadingTrackIds.value.add(track.clientId)

  try {
    const [blob] = await Promise.all([
      apiService.downloadTrackGpx(track.clientId, { includeRoutePoints }),
      new Promise((resolve) => setTimeout(resolve, 500)),
    ])

    const downloadName = (track.name?.trim() || `Track ${trackIndex + 1}`).trim()
    const fileName = `${downloadName}.gpx`
    await downloadFile(blob, fileName, {
      mimeType: 'application/gpx+xml',
    })
  } finally {
    downloadingTrackIds.value.delete(track.clientId)
  }
}

const closeTrackDownloadModal = () => {
  isTrackDownloadModalOpen.value = false
  downloadTrackIndex.value = null
}

const handleDownloadTrackGpx = async (options: { includeRoutePoints: boolean }) => {
  const trackIndex = downloadTrackIndex.value
  if (trackIndex === null) {
    return
  }

  const track = props.tour.tracks[trackIndex]
  if (!track || !track.clientId) {
    return
  }

  const shouldCloseAfterDownload = openTrackToolbarIds.value.has(trackIndex)

  await downloadTrackGpx(trackIndex, options.includeRoutePoints)
  closeTrackDownloadModal()
  if (shouldCloseAfterDownload) {
    openTrackToolbarIds.value.delete(trackIndex)
  }
}

const closeEditTrackModal = () => {
  isEditTrackModalOpen.value = false
}

const saveTrackChanges = async (
  tourId: string,
  trackIndex: number,
  updates: { name: string; color: string },
) => {
  if (!canEditTourContent.value) return
  if (!(await requirePermission('edit_track'))) return
  const tour = props.tour
  if (tour && trackIndex >= 0 && trackIndex < tour.tracks.length) {
    const trackId = tour.tracks[trackIndex].clientId
    workspaceStore.updateTrack(tourId, trackIndex, {
      ...updates,
      clientId: trackId,
    })
  }
  closeEditTrackModal()
}

const handleTrackDelete = async (tourId: string, trackIndex: number) => {
  if (!canDeleteTourContent.value) return
  if (!(await requirePermission('delete_track'))) return
  const tour = workspaceStore.tours.find((t) => t.clientId === tourId)
  if (!tour || trackIndex < 0 || trackIndex >= tour.tracks.length) return

  const track = tour.tracks[trackIndex]
  const trackName = track.name || `Track ${trackIndex + 1}`

  const confirmed = await showConfirm({
    title: 'Delete Track',
    message: `Are you sure you want to delete "${trackName}"? This action cannot be undone.`,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    variant: 'danger',
  })

  if (confirmed) {
    await workspaceStore.deleteTrack(tourId, trackIndex)
  }

  closeEditTrackModal()
}

// Waypoint edit/delete handled in WaypointList

const calculateTourElevationGain = (): number | null => {
  if (hideTourAscent.value) return null

  let totalGain: number | null = null

  props.tour.tracks.forEach((track) => {
    const gain = getTrackElevationGain(track)
    if (gain !== null && gain !== undefined) {
      totalGain = (totalGain || 0) + gain
    }
  })

  return totalGain
}

const calculateTourElevationLoss = (): number | null => {
  let totalLoss: number | null = null

  props.tour.tracks.forEach((track) => {
    const loss = getTrackElevationLoss(track)
    if (loss !== null && loss !== undefined) {
      totalLoss = (totalLoss || 0) + loss
    }
  })

  return totalLoss
}

const hideTourAscent = computed(() => shouldHideAscentForTour(props.tour))

const getVisibleTrackElevationGain = (track: Track): number | null => {
  if (shouldHideAscentForTrack(track, props.tour.type)) return null
  return getTrackElevationGain(track)
}

const calculateTourAverageSpeed = (): number | null => {
  const distance = calculateTourDistance() // Distance in meters
  const timeMoving = calculateTourTimeMoving() // Time in seconds

  if (!timeMoving || timeMoving === 0 || distance === 0) {
    return null
  }

  // Convert to km/h: (distance in meters / 1000) / (time in seconds / 3600)
  const speedKmh = distance / 1000 / (timeMoving / 3600)
  return speedKmh
}

const hasTourStats = computed(
  () =>
    calculateTourDistance() > 0 ||
    calculateTourElevationGain() !== null ||
    calculateTourElevationLoss() !== null ||
    calculateTourTimeElapsed() !== null ||
    calculateTourTimeMoving() !== null ||
    calculateTourAverageSpeed() !== null,
)

const calculateTourStartTime = (): number | null => {
  // Find the earliest start time across all tracks
  let earliestTime: number | null = null

  props.tour.tracks.forEach((track) => {
    if (track.startTime !== null && track.startTime !== undefined) {
      if (earliestTime === null || track.startTime < earliestTime) {
        earliestTime = track.startTime
      }
    }
  })

  return earliestTime
}

const calculateTourEndTime = (): number | null => {
  // Find the latest end time across all tracks
  let latestTime: number | null = null

  props.tour.tracks.forEach((track) => {
    if (track.endTime !== null && track.endTime !== undefined) {
      if (latestTime === null || track.endTime > latestTime) {
        latestTime = track.endTime
      }
    }
  })

  return latestTime
}

const formatActivityDateLine = (timestamp: number): string => {
  const date = new Date(timestamp)
  const weekday = date.toLocaleDateString(undefined, { weekday: 'long' })
  const time = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })
  const calendarDate = date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

  return `${weekday} at ${time}, ${calendarDate}`
}

// Activity: timestamp in ms from track startTime. Route: ISO string from tour.createdAt.
const tourIsActivity = computed(() => calculateTourStartTime() !== null)

const tourDateDisplay = computed((): string => {
  const startTime = calculateTourStartTime()
  if (startTime !== null) {
    return formatActivityDateLine(startTime)
  }
  if (props.tour.createdAt) {
    return new Date(props.tour.createdAt).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }
  return ''
})

const tourDateTooltip = computed((): string => {
  const startTime = calculateTourStartTime()
  const fmt = (ts: number) =>
    new Date(ts).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  if (startTime !== null) {
    const end = calculateTourEndTime()
    return end ? `${fmt(startTime)} – ${fmt(end)}` : fmt(startTime)
  }
  if (props.tour.createdAt) return fmt(new Date(props.tour.createdAt).getTime())
  return ''
})

const tourDateLine = computed(() => {
  if (!tourDateDisplay.value) return ''
  if (tourIsActivity.value) return tourDateDisplay.value
  return `Route added on ${tourDateDisplay.value}`
})

const toggleTrackToolbar = (trackIndex: number, event: Event) => {
  event.stopPropagation()
  if (openTrackToolbarIds.value.has(trackIndex)) {
    openTrackToolbarIds.value.delete(trackIndex)
  } else {
    openTrackToolbarIds.value.clear()
    openTrackToolbarIds.value.add(trackIndex)
  }
}

const toggleTrackVisibilityFromToolbar = (trackIndex: number) => {
  const track = props.tour.tracks[trackIndex]
  if (!track) return
  const newVisibility = track.visible === undefined ? false : !track.visible
  void workspaceStore.setTrackVisibility(props.tour.clientId, trackIndex, newVisibility)
  openTrackToolbarIds.value.delete(trackIndex)
}

const openEditTrackFromToolbar = (tourId: string, trackIndex: number) => {
  if (!canEditTourContent.value) return
  editingTrackIndex.value = trackIndex
  isEditTrackModalOpen.value = true
  openTrackToolbarIds.value.delete(trackIndex)
}

const enterTrackEditFromToolbar = (trackClientId?: string) => {
  if (!canEditTourContent.value) return
  if (!trackClientId || isRouteEditButtonDisabled()) return
  emit('enter-track-edit', props.tour.clientId, trackClientId)
  openTrackToolbarIds.value.clear()
}

const openTrackDownloadModalFromToolbar = (trackIndex: number) => {
  const track = props.tour.tracks[trackIndex]
  if (!track || !track.clientId || downloadingTrackIds.value.has(track.clientId)) return
  downloadTrackIndex.value = trackIndex
  isTrackDownloadModalOpen.value = true
}

const deleteTrackFromToolbar = async (tourId: string, trackIndex: number) => {
  if (!canDeleteTourContent.value) return
  if (!(await requirePermission('delete_track'))) return

  const tour = workspaceStore.tours.find((candidate) => candidate.clientId === tourId)
  if (!tour || trackIndex < 0 || trackIndex >= tour.tracks.length) return

  const track = tour.tracks[trackIndex]
  const trackName = track.name || `Track ${trackIndex + 1}`
  const confirmed = await showConfirm({
    title: 'Delete Track',
    message: `Are you sure you want to delete "${trackName}"? This action cannot be undone.`,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    variant: 'danger',
  })

  if (!confirmed) return

  await workspaceStore.deleteTrack(tourId, trackIndex)
  openTrackToolbarIds.value.delete(trackIndex)
}

const isTrackToolbarOpen = (trackIndex: number): boolean => {
  return openTrackToolbarIds.value.has(trackIndex)
}

// Waypoint toolbars handled by WaypointList

// ── Quick action button handlers ─────────────────────────────────────────────

const handleRedrawRoute = async () => {
  if (!canEditTourContent.value) return
  if (!(await requirePermission('edit_track'))) return
  if (props.tour.tracks.length === 0) {
    window.dispatchEvent(
      new CustomEvent('start-track-drawing', { detail: { tourId: props.tour.clientId } }),
    )
    return
  }
  if (props.tour.tracks.length === 1) {
    const trackClientId = props.tour.tracks[0]?.clientId
    if (trackClientId) emit('enter-track-edit', props.tour.clientId, trackClientId)
    return
  }
  await showConfirm({
    title: 'Select a track in Tracks',
    message:
      'This tour has multiple tracks. Open the Tracks panel and start route editing for the track you want to edit.',
    confirmText: 'OK',
    cancelText: undefined,
    variant: 'info',
  })
}

const isRedrawingAsRoute = ref(false)

const handleRedrawAsRoute = async () => {
  if (isRedrawingAsRoute.value) return
  if (!canEditTourContent.value) return
  if (!(await requirePermission('edit_track'))) return
  isRedrawingAsRoute.value = true
  try {
    const newTourId = await workspaceStore.addEmptyTour({
      name: `${props.tour.name} (copy)`,
      type: undefined,
      sourceTourClientId: getRootSourceTourClientId(props.tour),
      sourceTour: getRootSourceTour(props.tour),
      emitAddedEvent: false,
    })
    const duplicatedTrackIds = new Map<string, string>()
    for (const track of props.tour.tracks) {
      const newTrackId = await workspaceStore.addTrack(newTourId, {
        name: track.name ?? '',
        segments: track.segments.map(({ clientId: _cid, ...seg }) => ({
          ...seg,
          points: seg.points.map((p) => ({ ...p })),
        })),
        color: track.color,
        routingProfile: undefined,
      })
      if (newTrackId) duplicatedTrackIds.set(track.clientId, newTrackId)
    }
    for (const routePoint of props.tour.routePoints) {
      const newTrackClientId = duplicatedTrackIds.get(routePoint.trackClientId)
      if (!newTrackClientId) continue
      await workspaceStore.addRoutePoint(newTourId, {
        lat: routePoint.lat,
        lon: routePoint.lon,
        ele: routePoint.ele,
        name: routePoint.name,
        description: routePoint.description,
        order: routePoint.order,
        trackClientId: newTrackClientId,
      })
    }
    workspaceStore.setSelectedTourId(newTourId)
    const firstOriginalTrackId = props.tour.tracks[0]?.clientId
    const newFirstTrackId = firstOriginalTrackId
      ? duplicatedTrackIds.get(firstOriginalTrackId)
      : undefined
    if (newFirstTrackId) {
      emit('enter-track-edit', newTourId, newFirstTrackId)
    } else {
      window.dispatchEvent(
        new CustomEvent('start-track-drawing', { detail: { tourId: newTourId } }),
      )
    }
  } catch (err) {
    logService.error(LOG_CATEGORIES.GENERAL, 'Failed to re-draw as route', { error: err })
    showError('Failed to re-draw as route')
  } finally {
    isRedrawingAsRoute.value = false
  }
}

const isDuplicating = ref(false)

const handleDuplicate = async () => {
  if (isDuplicating.value) return
  if (!canEditTourContent.value) return
  if (!(await requirePermission('edit_track'))) return
  isDuplicating.value = true
  try {
    const newTourId = await workspaceStore.addEmptyTour({
      name: `${props.tour.name} (copy)`,
      type: props.tour.type ?? undefined,
      sourceTourClientId: getRootSourceTourClientId(props.tour),
      sourceTour: getRootSourceTour(props.tour),
      emitAddedEvent: false,
    })
    const duplicatedTrackIds = new Map<string, string>()
    for (const track of props.tour.tracks) {
      const newTrackId = await workspaceStore.addTrack(newTourId, {
        name: track.name ?? '',
        segments: track.segments.map(({ clientId: _cid, ...seg }) => ({
          ...seg,
          points: seg.points.map((p) => ({ ...p })),
        })),
        color: track.color,
        routingProfile: track.routingProfile,
      })
      if (newTrackId) duplicatedTrackIds.set(track.clientId, newTrackId)
    }
    for (const routePoint of props.tour.routePoints) {
      const newTrackClientId = duplicatedTrackIds.get(routePoint.trackClientId)
      if (!newTrackClientId) continue
      await workspaceStore.addRoutePoint(newTourId, {
        lat: routePoint.lat,
        lon: routePoint.lon,
        ele: routePoint.ele,
        name: routePoint.name,
        description: routePoint.description,
        order: routePoint.order,
        trackClientId: newTrackClientId,
      })
    }
    logService.info(LOG_CATEGORIES.GENERAL, 'Tour duplicated from detail panel', {
      tourId: props.tour.clientId,
      newTourId,
    })
  } catch (err) {
    logService.error(LOG_CATEGORIES.GENERAL, 'Failed to duplicate tour', { error: err })
    showError('Failed to duplicate tour')
  } finally {
    isDuplicating.value = false
  }
}

// Tour download
const isTourDownloadModalOpen = ref(false)
const isTourDownloading = ref(false)

const openTourDownloadModal = () => {
  isTourDownloadModalOpen.value = true
}

const closeTourDownloadModal = () => {
  isTourDownloadModalOpen.value = false
}

const handleDownloadTourGpx = async (options: { includeRoutePoints: boolean }) => {
  isTourDownloading.value = true
  try {
    const [blob] = await Promise.all([
      apiService.downloadGpx(props.tour.clientId, {
        includeRoutePoints: options.includeRoutePoints,
      }),
      new Promise((resolve) => setTimeout(resolve, 500)),
    ])
    const downloadName = props.isLibraryContext
      ? props.tour.name.trim() || 'tour'
      : getWorkspaceTourDownloadName(props.tour)
    await downloadFile(blob, `${downloadName}.gpx`, { mimeType: 'application/gpx+xml' })
  } catch (err) {
    logService.error(LOG_CATEGORIES.GENERAL, 'Failed to download tour GPX', { error: err })
    showError('Failed to download tour')
  } finally {
    isTourDownloading.value = false
    closeTourDownloadModal()
  }
}

// ── Add to collection ────────────────────────────────────────────────────────

const openAddToCollection = () => {
  if (!authStore.isAuthenticated) {
    window.dispatchEvent(new CustomEvent('open-auth-modal', { detail: { view: 'login' } }))
    return
  }
  openAddToCollectionPicker({ tours: [tourToAddToCollectionItem(props.tour)] })
}

const handleRemoveTour = async () => {
  if (!(await requirePermission('delete_tour'))) return
  const confirmed = await showConfirm({
    title: 'Remove Tour',
    message: `Are you sure you want to remove "${effectiveTourName.value}" from this workspace? It will still be available in your other workspaces.`,
    confirmText: 'Remove',
    cancelText: 'Cancel',
    variant: 'danger',
  })
  if (confirmed) {
    workspaceStore.clearTour(props.tour.clientId)
    workspaceStore.setSelectedTourId(null)
    emit('back')
  }
}

const handleDeleteTour = async () => {
  if (!(await requirePermission('delete_tour'))) return
  // Check creator-only content edit *before* opening the "are you sure" modal — otherwise the
  // explainer dialog fires from inside that modal's confirm handler and, since dialogService
  // only supports one dialog at a time, it never becomes visible and the confirm button hangs
  // forever on "Deleting…".
  if (!(await requireItemEditable(props.tour.canEdit, 'activity', props.tour.createdByName))) return
  isDeleteTourModalOpen.value = true
}

const closeDeleteTourModal = () => {
  if (isDeletingTour.value) return
  isDeleteTourModalOpen.value = false
}

const confirmDeleteTour = async () => {
  isDeletingTour.value = true
  try {
    await libraryStore.deleteTour(props.tour.clientId)
    const tourIndex = workspaceStore.tours.findIndex(
      (candidate) => candidate.clientId === props.tour.clientId,
    )
    if (tourIndex !== -1) {
      workspaceStore.tours.splice(tourIndex, 1)
    }
    workspaceStore.setSelectedTourId(null)
    isDeleteTourModalOpen.value = false
    emit('back')
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to delete tour from TourDetailPanel', {
      error,
      tourId: props.tour.clientId,
    })
    showError(`Failed to delete tour: ${error instanceof Error ? error.message : String(error)}`)
  } finally {
    isDeletingTour.value = false
  }
}

const closeAllToolbars = (event: Event) => {
  const target = event.target as HTMLElement
  if (!target.closest('[data-toolbar]') && !target.closest('[data-toolbar-toggle]')) {
    openTrackToolbarIds.value.clear()
    // Waypoint toolbars handled by WaypointList
  }
  if (!target.closest('[data-tour-color-picker]') && !target.closest('[data-tour-color-button]')) {
    isTourColorPickerOpen.value = false
  }
}

useEventListener(document, 'click', closeAllToolbars)
</script>

<template>
  <div :class="embedded ? '' : 'flex min-h-0 flex-1 flex-col overflow-hidden'">
    <div v-if="!embedded" class="shrink-0 px-4 pt-3 pb-1">
      <div class="relative flex items-center justify-center">
        <div v-if="selectedWaypoint" class="absolute left-0 top-1/2 -translate-y-1/2">
          <button
            @click="handleCloseWaypointDetail"
            class="w-7 h-7 flex items-center justify-center rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors hover:cursor-pointer"
            title="Back to tour"
            aria-label="Back to tour"
          >
            <ChevronRightIcon class="w-5 h-5 rotate-180" />
          </button>
        </div>
        <div class="flex items-center justify-center gap-0.5">
          <button
            @click="handleHeaderCenter"
            class="w-7 h-7 flex items-center justify-center rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors hover:cursor-pointer"
            title="Center on map"
          >
            <MaximizeIcon class="w-5 h-5" />
          </button>
          <button
            @click="handleHeaderVisibilityToggle"
            class="w-7 h-7 flex items-center justify-center rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors hover:cursor-pointer"
            :title="headerVisibilityTitle"
          >
            <component :is="headerVisibilityIcon" class="w-5 h-5" />
          </button>
        </div>
        <div class="absolute right-0 top-1/2 -translate-y-1/2">
          <button
            @click="goBack"
            class="w-7 h-7 shrink-0 flex items-center justify-center rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors hover:cursor-pointer"
            aria-label="Close panel"
            title="Close panel"
          >
            <XIcon class="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
    <Transition
      :name="panelContentTransitionName"
      mode="out-in"
      @before-enter="prepareTourScrollPosition"
      @enter="restorePreparedTourScrollPosition"
      @enter-cancelled="resetPreparedTourVisibility"
      @after-enter="finalizeTourScrollRestore"
    >
      <div
        v-if="selectedWaypoint"
        :key="waypointDetailKey"
        class="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        <POIDetailPanel
          mode="tour-waypoint"
          :waypoint="selectedWaypoint"
          :tour-id="tour.clientId"
          :tour-name="effectiveTourName"
          :waypoint-index="props.selectedWaypointIndex"
          :is-library-context="props.isLibraryContext"
          :waypoint-visible="isSelectedWaypointIndividuallyVisible"
          :show-header-controls="false"
          @back="handleCloseWaypointDetail"
          @close="goBack"
          @zoom-waypoint="handleZoomWaypoint"
          @toggle-waypoint-visibility="toggleWaypointVisibility"
          @delete-waypoint="handleDeleteWaypointRequest"
          class="min-h-0 flex-1"
        />
      </div>

      <div
        v-else
        key="tour-detail"
        data-tour-detail-panel-root
        :class="embedded ? '' : 'flex min-h-0 flex-1 flex-col overflow-hidden'"
      >
        <!-- In embedded mode the Route sheet's header owns the name + color dot. -->
        <div v-if="!embedded" class="shrink-0">
          <div class="relative mt-2">
            <div
              class="mx-auto grid w-full max-w-[calc(100%-2rem)] grid-cols-[1fr_auto_1fr] items-start"
            >
              <div class="relative flex justify-end pr-2 pt-[7px]">
                <button
                  ref="tourColorButtonRef"
                  type="button"
                  class="flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-colors"
                  :class="
                    canChangeTourColor
                      ? 'hover:cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1'
                      : 'cursor-default'
                  "
                  :title="tourColorButtonTitle"
                  :aria-label="tourColorButtonTitle"
                  :disabled="!canChangeTourColor"
                  data-tour-color-button
                  @click.stop="toggleTourColorPicker"
                >
                  <span
                    class="h-3.5 w-3.5 rounded-full border border-white shadow-sm"
                    :style="{ backgroundColor: tourColor }"
                  ></span>
                </button>

                <div
                  v-if="false"
                  class="absolute right-0 top-full z-20 mt-2 w-52 rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
                  data-tour-color-picker
                  @click.stop
                >
                  <div class="mb-2 text-xs font-medium text-gray-600">Tour color</div>
                  <div class="grid grid-cols-5 gap-2" role="radiogroup" aria-label="Tour color">
                    <button
                      v-for="color in tourAccentColors"
                      :key="color.value"
                      type="button"
                      class="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 hover:cursor-pointer"
                      :class="
                        tourColor === color.value ? 'ring-2 ring-primary-500 ring-offset-1' : ''
                      "
                      :style="{ backgroundColor: color.value }"
                      :title="color.label"
                      :aria-label="color.label"
                      :aria-checked="tourColor === color.value"
                      role="radio"
                      @click.stop="selectTourColor(color.value)"
                    >
                      <span
                        v-if="tourColor === color.value"
                        class="text-xs font-bold"
                        :style="{ color: getColorTextColor(color.value) }"
                        >✓</span
                      >
                    </button>
                  </div>
                </div>
              </div>

              <div class="group/title min-w-0">
                <div class="flex items-center justify-center gap-2">
                  <div
                    class="flex min-w-0 flex-1 items-baseline justify-center gap-1 rounded px-1.5 py-0.5 text-center transition-colors"
                    :class="
                      isEditingName
                        ? 'ring-1 ring-emerald-500 bg-white'
                        : canEditTourContent
                          ? 'hover:ring-1 hover:ring-gray-300 cursor-text'
                          : ''
                    "
                    @click="canEditTourContent && !isEditingName && startEditingName()"
                  >
                    <input
                      v-if="isEditingName"
                      ref="nameInputRef"
                      v-model="editedName"
                      class="min-w-0 flex-1 bg-transparent text-center text-lg font-semibold text-gray-900 outline-none"
                      :placeholder="tourNameEditPlaceholder"
                      @keydown.enter.prevent="saveTourName"
                      @keydown.esc="cancelEditingName"
                      @blur="saveTourName"
                    />
                    <h2
                      v-else
                      class="min-w-0 truncate text-xl font-semibold text-gray-900"
                      :title="effectiveTourName"
                      data-testid="tour-title"
                    >
                      {{ effectiveTourName }}
                    </h2>
                    <span
                      v-if="!isEditingName && tourCreatorByline"
                      class="shrink-0 text-xs font-normal text-gray-500"
                      data-testid="tour-creator-byline"
                    >
                      {{ tourCreatorByline }}
                    </span>
                  </div>
                  <span
                    v-if="!isEditingName && hasCollectionNameOverride(props.tour)"
                    class="shrink-0 text-gray-400"
                    :title="tourNameSubtitle ?? undefined"
                    aria-label="Custom name"
                  >
                    <TagIcon class="h-4 w-4" />
                  </span>
                </div>
                <div
                  v-if="tourNameSubtitle || (hasCollectionNameReset && canEditTourContent)"
                  class="mt-1 flex items-center justify-center gap-1.5"
                >
                  <p
                    v-if="tourNameSubtitle"
                    class="truncate text-center text-xs text-gray-500"
                    :title="tourNameSubtitle"
                    data-testid="tour-name-subtitle"
                  >
                    {{ tourNameSubtitle }}
                  </p>
                  <button
                    v-if="hasCollectionNameReset && canEditTourContent"
                    class="shrink-0 rounded-full p-1 text-gray-500 transition-colors hover:bg-black/5 hover:text-gray-800 hover:cursor-pointer"
                    title="Reset name"
                    aria-label="Reset name"
                    data-testid="tour-name-reset"
                    @click.stop="resetCollectionName"
                  >
                    <Undo2Icon class="h-3 w-3" />
                  </button>
                </div>
              </div>
              <div aria-hidden="true"></div>
            </div>
          </div>
        </div>

        <div
          ref="tourContentScrollRef"
          data-tour-detail-scroll
          :class="embedded ? '' : 'min-h-0 flex-1 overflow-y-auto'"
        >
          <div
            v-if="tourDateDisplay"
            :title="tourDateTooltip"
            class="mt-1 text-center text-sm text-gray-600"
            data-testid="tour-date"
          >
            {{ tourDateLine }}
          </div>
          <div
            v-if="tourIsActivity"
            class="mt-1 flex items-center justify-center text-sm text-gray-900 leading-snug"
            data-testid="tour-type-row"
          >
            <button
              v-if="canEditTourContent && isSmallScreen"
              type="button"
              class="mx-4 flex min-h-11 min-w-0 max-w-sm flex-1 items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors hover:bg-gray-100"
              aria-label="Edit activity type"
              data-testid="mobile-tour-type-edit"
              @click="openMobileActivityTypeSheet"
            >
              <component :is="tourTypeIcon" class="h-4 w-4 shrink-0 text-gray-500" />
              <span class="flex min-w-0 items-center gap-1.5">
                <span class="truncate">{{ tourTypeDisplay }}</span>
                <PencilIcon class="h-4 w-4 shrink-0 text-gray-400" />
              </span>
            </button>
            <div
              v-else-if="canEditTourContent"
              class="flex w-full max-w-sm items-center gap-2 px-4"
            >
              <component :is="tourTypeIcon" class="h-4 w-4 shrink-0 text-gray-500" />
              <TourTypeSelect
                v-model="tourType"
                class="tour-activity-type-select"
                label=""
                placeholder="Activity"
                @update:modelValue="handleTourTypeChange"
              />
            </div>
            <div v-else class="flex items-center gap-2 px-4">
              <component :is="tourTypeIcon" class="h-4 w-4 shrink-0 text-gray-500" />
              <span>{{ tourTypeDisplay }}</span>
            </div>
          </div>

          <!-- Quick action buttons (below name) -->
          <div class="px-3 pt-4 pb-2" data-testid="quick-actions">
            <div class="flex justify-center gap-2">
              <BaseActionButton
                :icon="tour.isPinned ? PinOffIcon : PinIcon"
                :title="tour.isPinned ? 'Unpin' : 'Pin'"
                :class="
                  tour.isPinned
                    ? '!bg-amber-50 !text-amber-700 ring-1 ring-amber-200 hover:!bg-amber-100 hover:!text-amber-800'
                    : ''
                "
                @click="handleTogglePinned"
              >
                {{ tour.isPinned ? 'Unpin' : 'Pin' }}
              </BaseActionButton>

              <!-- Re-draw as route (activity) -->
              <BaseActionButton
                v-if="isTourActivity && canEditTourContent"
                :icon="RouteIcon"
                :loading="isRedrawingAsRoute"
                :disabled="isRedrawingAsRoute"
                title="Edit as route"
                @click="handleRedrawAsRoute"
              >
                Edit as<br />route
              </BaseActionButton>

              <!-- Re-draw (route) -->
              <BaseActionButton
                v-if="!isTourActivity && canEditTourContent"
                :icon="RouteIcon"
                title="Edit route"
                @click="handleRedrawRoute"
              >
                Edit route
              </BaseActionButton>

              <!-- Download -->
              <BaseActionButton
                :icon="DownloadIcon"
                :loading="isTourDownloading"
                :disabled="isTourDownloading"
                title="Download GPX"
                @click="openTourDownloadModal"
              >
                Download GPX
              </BaseActionButton>

              <!-- Add to collection -->
              <button
                @click="openAddToCollection"
                class="flex flex-col items-center justify-center gap-1 w-20 h-20 rounded-lg transition-colors hover:cursor-pointer text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200"
                title="Add to collection"
                data-add-to-collection-button
              >
                <SquarePlusIcon class="w-6 h-6 shrink-0" />
                <span class="text-xs leading-tight font-medium text-center"
                  >Add to<br />collection</span
                >
              </button>
            </div>
          </div>

          <!-- Divider -->
          <div class="mx-4 mt-3 mb-3 border-t border-gray-200"></div>

          <!-- Tour stats -->
          <div class="px-4 pt-3 pb-4">
            <!-- Loading spinner for stats -->
            <div v-if="loading" class="flex items-center justify-center py-6">
              <Loader2Icon class="w-5 h-5 text-gray-400 animate-spin" />
            </div>
            <template v-else>
              <!-- Embedded host (Route sheet) injects a deduped, profile-consistent stats row
                   in place of the multi-cell grid. -->
              <slot v-if="embedded" name="stats" />
              <div v-if="hasTourStats && !embedded" class="grid grid-cols-3 gap-y-3 gap-x-1">
                <div v-if="calculateTourDistance() > 0" class="flex items-center gap-1">
                  <RulerIcon class="w-3 h-3 text-gray-500 shrink-0" />
                  <p class="text-sm font-semibold text-gray-900 truncate">
                    {{ formatDistance(calculateTourDistance()) }}
                  </p>
                </div>
                <div v-if="calculateTourElevationGain() !== null" class="flex items-center gap-1">
                  <ArrowUpRightIcon class="w-3 h-3 text-gray-500 shrink-0" />
                  <p class="text-sm font-semibold text-gray-900">
                    +{{ Math.round(calculateTourElevationGain() || 0) }}m
                  </p>
                </div>
                <div v-if="calculateTourElevationLoss() !== null" class="flex items-center gap-1">
                  <ArrowDownRightIcon class="w-3 h-3 text-gray-500 shrink-0" />
                  <p class="text-sm font-semibold text-gray-900">
                    -{{ Math.round(calculateTourElevationLoss() || 0) }}m
                  </p>
                </div>
                <div v-if="calculateTourTimeElapsed() !== null" class="flex items-center gap-1">
                  <ClockIcon class="w-3 h-3 text-gray-500 shrink-0" />
                  <p class="text-sm font-semibold text-gray-900">
                    {{ formatTime(calculateTourTimeElapsed() || 0) }}
                  </p>
                </div>
                <div v-if="calculateTourTimeMoving() !== null" class="flex items-center gap-1">
                  <ClockIcon class="w-3 h-3 text-gray-400 shrink-0" />
                  <p class="text-sm font-semibold text-gray-900">
                    {{ formatTime(calculateTourTimeMoving() || 0) }}
                  </p>
                </div>
                <div v-if="calculateTourAverageSpeed() !== null" class="flex items-center gap-1">
                  <CircleSlash2Icon class="w-3 h-3 text-gray-500 shrink-0" />
                  <p class="text-sm font-semibold text-gray-900 truncate">
                    {{ calculateTourAverageSpeed()!.toFixed(1) }} km/h
                  </p>
                </div>
              </div>

              <!-- Tour description -->
              <div
                class="mt-3 rounded px-1.5 py-0.5 -mx-1.5 transition-colors"
                :class="
                  isEditingDescription
                    ? 'ring-1 ring-emerald-500 bg-white'
                    : canEditTourContent
                      ? 'hover:ring-1 hover:ring-gray-300 cursor-text'
                      : ''
                "
                @click="canEditTourContent && !isEditingDescription && startEditingDescription()"
              >
                <template v-if="!isEditingDescription">
                  <p
                    v-if="tour.description"
                    class="text-sm text-gray-900 whitespace-pre-wrap"
                    :class="descriptionExpanded ? '' : 'line-clamp-6'"
                  >
                    {{ tour.description }}
                  </p>
                  <p v-else-if="canEditTourContent" class="text-sm text-gray-400 italic">
                    Add a description...
                  </p>
                  <button
                    v-if="tour.description && !descriptionExpanded && tour.description.length > 300"
                    @click.stop="descriptionExpanded = true"
                    class="mt-1 text-xs text-emerald-600 hover:text-emerald-700 hover:cursor-pointer"
                  >
                    Show more
                  </button>
                  <button
                    v-if="tour.description && descriptionExpanded && tour.description.length > 300"
                    @click.stop="descriptionExpanded = false"
                    class="mt-1 text-xs text-emerald-600 hover:text-emerald-700 hover:cursor-pointer"
                  >
                    Show less
                  </button>
                </template>
                <template v-else>
                  <textarea
                    ref="descriptionTextareaRef"
                    v-model="editedDescription"
                    class="w-full text-sm rounded px-0 py-0 resize-none focus:outline-none overflow-hidden bg-transparent"
                    :class="descriptionError ? 'text-red-600' : ''"
                    rows="3"
                    placeholder="Add a description..."
                    @click.stop
                    @input="
                      (e) => {
                        const el = e.target as HTMLTextAreaElement
                        el.style.height = 'auto'
                        el.style.height = el.scrollHeight + 'px'
                      }
                    "
                    @keydown.enter.exact.prevent="saveTourDescription"
                    @keydown.shift.enter.stop
                    @keydown.esc="cancelEditingDescription"
                    @blur="saveTourDescription"
                  />
                  <p v-if="descriptionError" class="text-xs text-red-600 mt-1">
                    {{ descriptionError }}
                  </p>
                </template>
              </div>
            </template>
          </div>

          <!-- Tracks Section Header -->
          <template v-if="tour.tracks.length > 1 && !embedded">
            <div class="flex justify-between items-center px-4 pt-3 pb-1 group">
              <button
                @click="toggleTracksExpanded"
                class="flex items-center hover:text-primary-600"
              >
                <component
                  :is="isTracksExpanded ? ChevronDownIcon : ChevronRightIcon"
                  class="w-3 h-3 text-gray-500"
                />
                <span
                  class="text-base font-semibold text-gray-700 ml-1"
                  :class="{ 'text-gray-400': !isAnyTrackVisible }"
                >
                  Tracks ({{ tour.tracks.length }})
                </span>
              </button>
              <div class="flex items-center gap-1">
                <button
                  v-if="tour.tracks.length > 0"
                  @click="toggleAllTracksVisibility(!isAnyTrackVisible)"
                  class="p-0.5 text-gray-400 hover:text-gray-600"
                  :title="isAnyTrackVisible ? 'Hide all tracks' : 'Show all tracks'"
                  :class="
                    !isAnyTrackVisible
                      ? 'opacity-100'
                      : 'opacity-0 group-hover:opacity-100 transition-opacity duration-200'
                  "
                >
                  <component :is="isAnyTrackVisible ? EyeIcon : EyeOffIcon" class="w-3 h-3" />
                </button>
              </div>
            </div>
          </template>

          <template v-if="tour.tracks.length > 1 && isTracksExpanded && !embedded">
            <div class="px-4 pb-2">
              <div
                v-for="(track, index) in tour.tracks"
                :key="index"
                class="flex items-start py-1.5 hover:bg-gray-50 -mx-1 px-1 rounded cursor-pointer group/track"
                @click="handleZoomTrack(tour.clientId, index)"
              >
                <div
                  class="w-2.5 h-2.5 rounded-full mt-[3px] mr-2 shrink-0"
                  :style="{ backgroundColor: getTrackColor(track) }"
                ></div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between">
                    <span
                      class="text-sm font-medium text-gray-900 truncate"
                      :title="track.name || `Track ${index + 1}`"
                      :class="{ '!text-gray-400': !isTrackVisible(index) }"
                    >
                      {{ track.name || `Track ${index + 1}` }}
                    </span>
                    <div class="flex items-center ml-1 relative shrink-0">
                      <button
                        v-if="!isTrackVisible(index)"
                        @click.stop="toggleTrackVisibility(index, $event)"
                        class="p-1 text-gray-400 hover:text-gray-600 hover:cursor-pointer"
                        title="Show track"
                      >
                        <EyeOffIcon class="w-3 h-3" />
                      </button>
                      <button
                        @click.stop="toggleTrackToolbar(index, $event)"
                        class="p-1 text-gray-400 hover:text-gray-600 hover:cursor-pointer"
                        :title="isTrackToolbarOpen(index) ? 'Close toolbar' : 'Open toolbar'"
                        data-toolbar-toggle
                      >
                        <MoreHorizontalIcon class="w-3.5 h-3.5" />
                      </button>

                      <!-- Expandable toolbar -->
                      <div
                        v-if="isTrackToolbarOpen(index)"
                        class="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 flex items-center p-1.5 space-x-0.5"
                        data-toolbar
                      >
                        <TrackActionMenu
                          :track="track"
                          :is-visible="isTrackVisible(index)"
                          :can-route-edit="props.allowTrackRouteEdit === true"
                          :can-edit-track="canEditTourContent"
                          :can-delete-track="canDeleteTourContent"
                          :is-editing="isEditingTrack(track.clientId)"
                          :is-downloading="isTrackDownloading(track)"
                          :route-edit-title="getRouteEditButtonTitle(track)"
                          @toggle-visibility="toggleTrackVisibilityFromToolbar(index)"
                          @edit="openEditTrackFromToolbar(tour.clientId, index)"
                          @route-edit="enterTrackEditFromToolbar(track.clientId)"
                          @download="openTrackDownloadModalFromToolbar(index)"
                          @delete="deleteTrackFromToolbar(tour.clientId, index)"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="text-xs text-gray-500">
                    <span>{{ formatDistance(calculateTrackDistance(track)) }}</span>
                    <span v-if="getVisibleTrackElevationGain(track)">
                      ↑ {{ Math.round(getVisibleTrackElevationGain(track) || 0) }}m</span
                    >
                    <span v-if="getTrackElevationLoss(track)">
                      ↓ {{ Math.round(getTrackElevationLoss(track) || 0) }}m</span
                    >
                  </div>
                </div>
              </div>

              <div v-if="tour.tracks.length === 0" class="py-3 text-center text-xs text-gray-400">
                <p>No tracks available for this tour.</p>
              </div>
            </div>
          </template>

          <!-- Waypoint Section Header -->
          <div v-if="!embedded" class="flex justify-between items-center px-4 pt-3 pb-1 group">
            <button
              @click="isWaypointsExpanded = !isWaypointsExpanded"
              class="flex items-center flex-1 hover:text-primary-600 focus:outline-none hover:cursor-pointer"
            >
              <ChevronDownIcon v-if="isWaypointsExpanded" class="w-3 h-3 text-gray-500" />
              <ChevronRightIcon v-else class="w-3 h-3 text-gray-500" />
              <span class="text-base font-semibold text-gray-700 ml-1">
                Waypoints ({{ tour.waypoints.length }})
              </span>
            </button>
            <div class="flex items-center gap-1">
              <button
                v-if="canEditWaypointContent"
                @click="handleAddWaypoint"
                class="p-0.5 text-gray-400 hover:text-primary-600 hover:cursor-pointer"
                title="Add waypoint"
              >
                <PlusIcon class="w-3 h-3" />
              </button>
              <button
                @click="toggleAllWaypointsVisibility"
                class="flex items-center justify-center w-5 h-5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:cursor-pointer rounded transition-colors shrink-0"
                :title="effectiveWaypointVisibility ? 'Hide all waypoints' : 'Show all waypoints'"
                :aria-label="
                  effectiveWaypointVisibility ? 'Hide all waypoints' : 'Show all waypoints'
                "
              >
                <component
                  :is="effectiveWaypointVisibility ? EyeIcon : EyeOffIcon"
                  class="w-3.5 h-3.5"
                />
              </button>
              <button
                v-if="canDeleteWaypointContent"
                @click="deleteAllWaypoints"
                class="p-0.5 text-gray-400 hover:text-red-600 hover:cursor-pointer"
                title="Delete all waypoints"
              >
                <Trash2Icon class="w-3 h-3" />
              </button>
            </div>
          </div>

          <WaypointList
            v-if="!embedded"
            :tour="tour"
            :show-header="false"
            :expanded="isWaypointsExpanded"
            :can-edit-waypoints="canEditWaypointContent"
            :can-delete-waypoints="canDeleteWaypointContent"
            @update:expanded="(val: boolean) => (isWaypointsExpanded = val)"
            @zoom-waypoint="handleZoomWaypoint"
            @open-waypoint-detail="handleOpenWaypointDetail"
          />

          <!-- Route Points Section -->
          <div v-if="showRoutePointsSection" class="mx-4 border-t border-gray-200"></div>
          <RoutePointList
            v-if="showRoutePointsSection"
            :tour="tour"
            :expanded="isRoutePointsExpanded"
            :track-client-id="uiStore.editingTrackClientId"
            :can-delete-route-points="canDeleteWaypointContent"
            @update:expanded="(val: boolean) => (isRoutePointsExpanded = val)"
            @zoom-route-point="handleZoomRoutePoint"
          />
        </div>

        <!-- Secondary actions (stacked) -->
        <div
          class="shrink-0 border-t border-gray-200 bg-white/95 px-4 py-1 backdrop-blur-sm"
          data-testid="secondary-actions"
        >
          <div class="flex flex-col">
            <BaseActionButton
              v-if="!isTourActivity && canEditTourContent"
              layout="row"
              :icon="CopyIcon"
              :loading="isDuplicating"
              :disabled="isDuplicating"
              title="Duplicate route"
              @click="handleDuplicate"
            >
              Duplicate route
            </BaseActionButton>
            <a
              v-if="sourceTour"
              class="flex items-center gap-2.5 py-2 text-sm text-gray-900 hover:text-gray-900 hover:cursor-pointer transition-colors w-full"
              :href="sourceTourHref"
              :title="'Open source: ' + sourceTour.name"
              @click="handleSourceTourClick"
            >
              <ArrowUpRightIcon class="w-4 h-4 shrink-0" />
              <span>Based on {{ sourceTour.name }}</span>
            </a>
            <a
              v-else-if="sourceFileLabel"
              class="flex items-center gap-2.5 py-2 text-sm text-gray-900 hover:text-gray-900 hover:cursor-pointer transition-colors w-full"
              :href="sourceFileHref"
              :download="sourceFileDownloadName"
              :title="'Download source file: ' + sourceFileLabel"
            >
              <DownloadIcon class="w-4 h-4 shrink-0" />
              <span>Download unmodified original</span>
            </a>
            <BaseActionButton
              v-if="canRemoveTourFromCollection"
              layout="row"
              variant="danger"
              :icon="SquareMinusIcon"
              title="Remove from collection"
              @click="handleRemoveTour"
            >
              Remove from collection
            </BaseActionButton>
            <BaseActionButton
              v-if="canDeleteEntireTour"
              layout="row"
              variant="danger"
              :icon="Trash2Icon"
              title="Delete tour"
              @click="handleDeleteTour"
            >
              Delete
            </BaseActionButton>
          </div>
        </div>
      </div>
    </Transition>

    <MobileActivityTypeActionSheet
      v-if="isSmallScreen"
      :open="isMobileActivityTypeSheetOpen"
      :current-type="tourType"
      :saving="isSavingMobileActivityType"
      :error="mobileActivityTypeError"
      @close="closeMobileActivityTypeSheet"
      @confirm="saveMobileActivityType"
    />

    <!-- Track edit modal - teleported to body to avoid container constraints -->
    <Teleport to="body">
      <TrackEditModal
        :is-open="isEditTrackModalOpen"
        :tour-id="props.tour.clientId"
        :track-index="editingTrackIndex"
        @close="closeEditTrackModal"
        @save="saveTrackChanges"
        @delete-track="handleTrackDelete"
      />
    </Teleport>

    <!-- Waypoint editing handled in WaypointList component -->

    <!-- Track download + GPX modal -->
    <Teleport to="body">
      <GpxDownloadOptionsModal
        :is-open="isTrackDownloadModalOpen"
        title="Download Track GPX"
        :is-downloading="isTrackDownloadModalDownloading"
        @close="closeTrackDownloadModal"
        @download="handleDownloadTrackGpx"
      />
    </Teleport>

    <!-- Tour download modal -->
    <Teleport to="body">
      <GpxDownloadOptionsModal
        :is-open="isTourDownloadModalOpen"
        title="Download Tour GPX"
        :is-downloading="isTourDownloading"
        @close="closeTourDownloadModal"
        @download="handleDownloadTourGpx"
      />
    </Teleport>

    <Teleport to="body">
      <TourDeleteModal
        :open="isDeleteTourModalOpen"
        :tour="props.tour"
        :tour-name="effectiveTourName"
        :deleting="isDeletingTour"
        @close="closeDeleteTourModal"
        @confirm="confirmDeleteTour"
      />
    </Teleport>

    <Teleport to="body">
      <div
        v-if="isTourColorPickerOpen"
        :style="tourColorPickerStyle"
        class="fixed z-9999 w-52 rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
        data-tour-color-picker
        @click.stop
      >
        <div class="mb-2 text-xs font-medium text-gray-600">Tour color</div>
        <div class="grid grid-cols-5 gap-2" role="radiogroup" aria-label="Tour color">
          <button
            v-for="color in tourAccentColors"
            :key="color.value"
            type="button"
            class="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 hover:cursor-pointer"
            :class="tourColor === color.value ? 'ring-2 ring-primary-500 ring-offset-1' : ''"
            :style="{ backgroundColor: color.value }"
            :title="color.label"
            :aria-label="color.label"
            :aria-checked="tourColor === color.value"
            role="radio"
            @click.stop="selectTourColor(color.value)"
          >
            <span
              v-if="tourColor === color.value"
              class="text-xs font-bold"
              :style="{ color: getColorTextColor(color.value) }"
              >✓</span
            >
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.tour-detail-drill-forward-enter-active,
.tour-detail-drill-forward-leave-active,
.tour-detail-drill-back-enter-active,
.tour-detail-drill-back-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}

.tour-detail-drill-forward-enter-from,
.tour-detail-drill-back-leave-to {
  opacity: 0;
  transform: translateX(16px);
}

.tour-detail-drill-forward-leave-to,
.tour-detail-drill-back-enter-from {
  opacity: 0;
  transform: translateX(-16px);
}

.tour-detail-drill-none-enter-active,
.tour-detail-drill-none-leave-active {
  transition: none;
}

.tour-activity-type-select :deep(div[tabindex='0']) {
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}
</style>
